import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Icon from "@/components/base/Icon";
import { chapterPanels } from "@/mocks/panels";
import { useReaderSettings, type ReaderFontSize, type ReaderTheme } from "@/hooks/useReaderSettings";
import { useReadingProgress } from "@/hooks/useReadingProgress";
import { useSeo } from "@/hooks/useSeo";
import { getChapters, getComicById } from "@/utils/comic";

const THEME_STYLES: Record<ReaderTheme, { wrap: string; bar: string; panel: string; chip: string }> = {
  light: {
    wrap: "bg-background-50 text-foreground-950",
    bar: "bg-background-50/95 border-background-200 text-foreground-900",
    panel: "bg-background-50 border-background-200",
    chip: "bg-background-100 text-foreground-700 hover:bg-primary-100",
  },
  sepia: {
    wrap: "bg-accent-50 text-accent-950",
    bar: "bg-accent-50/95 border-accent-200 text-accent-950",
    panel: "bg-accent-50 border-accent-200",
    chip: "bg-accent-100 text-accent-900 hover:bg-accent-200",
  },
  dark: {
    wrap: "bg-foreground-950 text-background-100",
    bar: "bg-foreground-900/95 border-foreground-800 text-background-100",
    panel: "bg-foreground-900 border-foreground-800",
    chip: "bg-foreground-800 text-background-100 hover:bg-foreground-700",
  },
};

const THEME_OPTIONS: { label: string; value: ReaderTheme; swatch: string }[] = [
  { label: "白底", value: "light", swatch: "bg-background-50 border-background-300" },
  { label: "护眼黄", value: "sepia", swatch: "bg-accent-50 border-accent-300" },
  { label: "深色", value: "dark", swatch: "bg-foreground-950 border-foreground-700" },
];

const FONT_OPTIONS: { label: string; value: ReaderFontSize }[] = [
  { label: "小", value: "sm" },
  { label: "中", value: "base" },
  { label: "大", value: "lg" },
];

const SUBTITLE_SIZE: Record<ReaderFontSize, string> = {
  sm: "text-xs",
  base: "text-sm",
  lg: "text-base",
};

export default function ReadPage() {
  const { comicId, chapterId } = useParams();
  const navigate = useNavigate();
  const comic = getComicById(comicId);
  const chapters = useMemo(() => (comic ? getChapters(comic) : []), [comic]);

  const [showChapters, setShowChapters] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { settings, setTheme, setFontSize } = useReaderSettings();
  const { saveProgress } = useReadingProgress();

  const currentIndex = chapters.findIndex((c) => c.id === chapterId);
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;
  const current = chapters[safeIndex];
  const storyIndex = current?.index || 1;
  const total = comic?.chapterCount || chapters.length;

  const prevChapter = chapters.find((c) => c.index === storyIndex - 1);
  const nextChapter = chapters.find((c) => c.index === storyIndex + 1);

  useEffect(() => {
    if (comic && current) {
      saveProgress(comic.id, current.id);
    }
  }, [comic, current, saveProgress]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowChapters(false);
    setShowSettings(false);
  }, [chapterId]);

  useSeo({
    title:
      comic && current
        ? `${comic.title} ${current.title} - 羞羞色漫在线阅读`
        : "在线阅读 - 羞羞色漫",
    description: comic
      ? `在线免费阅读《${comic.title}》${current?.title || ""}，羞羞色漫提供舒适的多设备漫画阅读体验。`
      : "",
    keywords: comic
      ? `${comic.title},${current?.title || ""},羞羞色漫,在线阅读,在线漫画`
      : undefined,
  });

  if (!comic || chapters.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background-50 px-4 text-center">
        <Icon name="ri-error-warning-line" className="w-12 h-12 text-foreground-400" />
        <p className="text-foreground-700">未找到该漫画或章节</p>
        <Link
          to="/"
          className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const theme = THEME_STYLES[settings.theme];

  return (
    <div className={`min-h-screen w-full ${theme.wrap}`}>
      <header
        className={`sticky top-0 z-40 w-full border-b backdrop-blur ${theme.bar}`}
      >
        <div className="w-full px-4 md:px-8 h-16 flex items-center gap-3">
          <Link
            to={`/comic/${comic.id}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${theme.chip}`}
          >
            <Icon name="ri-arrow-left-line" className="w-4 h-4" />
            <span className="hidden sm:inline">返回详情</span>
          </Link>

          <div className="min-w-0 flex-1 text-center md:text-left">
            <p className="font-heading text-sm md:text-base font-semibold truncate">
              {comic.title}
            </p>
            <p className={`${SUBTITLE_SIZE[settings.fontSize]} opacity-70 truncate`}>
              {current.title} · 第 {storyIndex} / {total} 话
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setShowChapters((v) => !v);
              setShowSettings(false);
            }}
            aria-label="章节目录"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${theme.chip}`}
          >
            <Icon name="ri-list-unordered" className="w-4 h-4" />
            <span className="hidden sm:inline">目录</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setShowSettings((v) => !v);
              setShowChapters(false);
            }}
            aria-label="阅读设置"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${theme.chip}`}
          >
            <Icon name="ri-settings-3-line" className="w-4 h-4" />
            <span className="hidden sm:inline">设置</span>
          </button>
        </div>

        {showSettings && (
          <div className={`border-t ${theme.panel} px-4 md:px-8 py-4 animate-fade-in`}>
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <p className="text-xs opacity-60 mb-2">阅读背景</p>
                <div className="flex flex-wrap gap-2">
                  {THEME_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setTheme(option.value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
                        settings.theme === option.value
                          ? "bg-primary-500 text-background-50"
                          : theme.chip
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-full border ${option.swatch}`} />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs opacity-60 mb-2">界面字号</p>
                <div className="flex flex-wrap gap-2">
                  {FONT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFontSize(option.value)}
                      className={`px-4 py-2 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
                        settings.fontSize === option.value
                          ? "bg-primary-500 text-background-50"
                          : theme.chip
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p className="mt-3 text-[11px] opacity-50">
              阅读设置会保存在本机浏览器，下次打开自动生效（演示功能）
            </p>
          </div>
        )}

        {showChapters && (
          <div
            className={`border-t ${theme.panel} px-4 md:px-8 py-4 max-h-[60vh] overflow-y-auto animate-fade-in`}
          >
            <p className="text-xs opacity-60 mb-3">章节目录（共 {chapters.length} 话）</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/read/${comic.id}/${chapter.id}`}
                  className={`px-2 py-2 rounded-md text-center text-xs truncate cursor-pointer transition-colors ${
                    chapter.id === current.id
                      ? "bg-primary-500 text-background-50"
                      : theme.chip
                  }`}
                >
                  {chapter.title}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="w-full px-2 sm:px-4 md:px-0 py-4 md:py-6">
        <div className="mx-auto w-full max-w-[860px] flex flex-col gap-3 md:gap-4">
          {chapterPanels.map((panel, index) => (
            <div
              key={panel}
              className="w-full rounded-lg overflow-hidden border border-black/5 bg-background-100"
            >
              <img
                src={panel}
                alt={`${comic.title} ${current.title} 第${index + 1}页`}
                title={`${comic.title} ${current.title}`}
                className="w-full h-auto object-contain block"
              />
            </div>
          ))}
        </div>

        <div className="mx-auto w-full max-w-[860px] mt-6 mb-2">
          <div className={`rounded-lg border ${theme.panel} p-4 text-center`}>
            <p className={`${SUBTITLE_SIZE[settings.fontSize]} opacity-70`}>
              已阅读到 {current.title} · 本章进度 {storyIndex} / {total} 话
            </p>
            <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
              <button
                type="button"
                disabled={!prevChapter}
                onClick={() =>
                  prevChapter && navigate(`/read/${comic.id}/${prevChapter.id}`)
                }
                className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  prevChapter
                    ? `cursor-pointer ${theme.chip}`
                    : "opacity-40 cursor-not-allowed border border-current"
                }`}
              >
                <Icon name="ri-arrow-left-line" className="w-4 h-4" />
                上一章
              </button>
              <Link
                to={`/comic/${comic.id}`}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-current text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:opacity-80"
              >
                <Icon name="ri-book-2-line" className="w-4 h-4" />
                作品详情
              </Link>
              <button
                type="button"
                disabled={!nextChapter}
                onClick={() =>
                  nextChapter && navigate(`/read/${comic.id}/${nextChapter.id}`)
                }
                className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  nextChapter
                    ? "bg-primary-500 text-background-50 cursor-pointer hover:bg-primary-600"
                    : "opacity-40 cursor-not-allowed border border-current"
                }`}
              >
                下一章
                <Icon name="ri-arrow-right-line" className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="mt-3 text-center text-[11px] opacity-50">
            演示提示：漫画图片为示例内容，章节与进度保存在本机浏览器，后续可接入真实数据接口
          </p>
        </div>
      </main>
    </div>
  );
}